"use client"

import { useEffect, useMemo, useRef, useState } from "react"
import { getCountries, getCountryCallingCode, parsePhoneNumberFromString, type CountryCode } from "libphonenumber-js/max"
import { useLocale, useTranslations } from "next-intl"
import { Input } from "@/components/ui/input"
import { TENANT_PHONE_CHARACTERS, TENANT_PHONE_MAX_LENGTH, TenantPhoneSchema } from "@/lib/auth/tenant-phone"

/** Keeps typing unformatted while emitting international values for shared server validation. */
export function ContactPhoneInput({ id, value, onChange, disabled = false }: {
  id: string
  value: string
  onChange: (value: string) => void
  disabled?: boolean
}) {
  const t = useTranslations("Common")
  const locale = useLocale()
  const [draft, setDraft] = useState(() => formatSavedPhone(value))
  const [country, setCountry] = useState<CountryCode | undefined>()
  const [touched, setTouched] = useState(false)
  const lastEmitted = useRef(value)
  const parsed = parsePhoneNumberFromString(draft, { defaultCountry: country, extract: false })
  const selectedCountry = parsed?.country ?? country
  const invalid = Boolean(value.trim()) && !TenantPhoneSchema.safeParse(value).success
  const showError = touched && invalid
  const countries = useMemo(() => {
    const names = new Intl.DisplayNames([locale], { type: "region" })
    return getCountries().map(code => ({ code, label: `${names.of(code) ?? code} (+${getCountryCallingCode(code)})` }))
      .sort((left, right) => left.label.localeCompare(right.label, locale))
  }, [locale])

  useEffect(() => {
    if (value === lastEmitted.current) return
    lastEmitted.current = value
    setDraft(formatSavedPhone(value))
    setCountry(undefined)
    setTouched(false)
  }, [value])

  function update(nextDraft: string, nextCountry = selectedCountry) {
    setDraft(nextDraft)
    const phone = parsePhoneNumberFromString(nextDraft, { defaultCountry: nextCountry, extract: false })
    let nextValue = nextDraft
    if (phone?.isValid() && TENANT_PHONE_CHARACTERS.test(nextDraft)) {
      nextValue = phone.number
    } else if (nextDraft.trim() && nextCountry && !nextDraft.trim().startsWith("+")) {
      nextValue = `+${getCountryCallingCode(nextCountry)} ${nextDraft}`
    }
    lastEmitted.current = nextValue
    onChange(nextValue)
  }

  function changeCountry(nextCountry: CountryCode | undefined) {
    setCountry(nextCountry)
    if (!nextCountry || !draft.trim()) return
    const national = parsed?.nationalNumber ?? draft.replace(/^\+/, "")
    update(`+${getCountryCallingCode(nextCountry)} ${national}`, nextCountry)
  }

  return (
    <div className="space-y-2">
      <div className="flex min-w-0 flex-wrap gap-2">
        <select
          aria-label={t("phoneCountryCode")}
          value={selectedCountry ?? ""}
          onChange={(event) => changeCountry((event.target.value || undefined) as CountryCode | undefined)}
          disabled={disabled}
          className="h-10 w-full min-w-0 rounded-md border border-input bg-background px-3 text-sm sm:flex-[1_1_12rem] disabled:opacity-50"
        >
          <option value="">{t("phoneCountryCode")}</option>
          {countries.map(({ code, label }) => <option key={code} value={code}>{label}</option>)}
        </select>
        <Input
          id={id}
          type="tel"
          autoComplete="tel"
          maxLength={TENANT_PHONE_MAX_LENGTH}
          value={draft}
          disabled={disabled}
          aria-invalid={showError}
          aria-describedby={`${id}-help`}
          onChange={(event) => update(event.target.value)}
          onBlur={() => {
            setTouched(true)
            if (!invalid && parsed?.isValid()) setDraft(parsed.formatInternational())
          }}
          className="min-w-0 flex-[2_1_10rem]"
        />
      </div>
      <p id={`${id}-help`} className={showError ? "text-sm text-destructive" : "text-sm text-muted-foreground"}>
        <span key={showError ? "error" : "hint"}>{showError ? t("phoneInvalid") : t("phoneHint")}</span>
      </p>
    </div>
  )
}

function formatSavedPhone(value: string): string {
  if (!TenantPhoneSchema.safeParse(value).success) return value
  return parsePhoneNumberFromString(value, { extract: false })?.formatInternational() ?? value
}
